import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { consumeTabCloseSuppression } from '../lib/tabCloseSuppression';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';
// Comfortably under tabPresence.js's own PRESENCE_TTL_SECONDS (45s) on
// the backend, same ratio IdleTimeoutWatcher.jsx uses for its own
// heartbeat-vs-TTL margin.
const HEARTBEAT_MS = 15 * 1000;
const TAB_ID_STORAGE_KEY = 'bescom_tab_id';

// sessionStorage, not a plain module-level variable: survives a refresh
// or in-app navigation (same tab, same id, so the close beacon those
// also fire gets cancelled out by this same tab's own next heartbeat —
// see tabPresence.js) but is never shared with a genuinely different
// tab, even one opened from this same page.
//
// CONFIRMED GAP (2026-10-13): unlike tabCloseSuppression.js's own
// sessionStorage access, this had no try/catch — some private-browsing
// configurations throw on sessionStorage access rather than just
// returning null, which would have been an uncaught exception silently
// breaking this component's whole effect (no heartbeat, no close signal
// at all) in that tab. Falls back to a fresh in-memory id for that one
// page load if storage is unavailable — this tab just won't survive a
// refresh with the same id, same degraded-but-not-broken behavior as
// the suppression helper's own try/catch.
function getTabId() {
  try {
    let id = sessionStorage.getItem(TAB_ID_STORAGE_KEY);
    if (!id) {
      id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
      sessionStorage.setItem(TAB_ID_STORAGE_KEY, id);
    }
    return id;
  } catch (_) {
    return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  }
}

// Logs out the whole shared SSO session once the LAST open tab across
// every connected app closes — see backend/src/lib/tabPresence.js and
// tabCloseSweep.js for the actual revocation + cross-app/cross-tab
// debounce logic. This component only ever sends two signals: "still
// open" on a timer, and "closing" once, via pagehide.
export default function TabPresenceWatcher() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return undefined;

    const tabId = getTabId();

    function heartbeat() {
      fetch(`${API_BASE}/account/tabs/heartbeat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tabId }),
      }).catch(() => {});
    }

    function sendCloseBeacon() {
      // CONFIRMED ROOT CAUSE (2026-10-12): pagehide fires on ANY
      // navigation away, not just a real close — see
      // tabCloseSuppression.js's own header comment for the incident
      // this caused. Every intentional hard navigation in this app
      // marks itself via navigateAway()/reloadPage() first.
      //
      // CONFIRMED GAP (2026-10-13): this used to return early and send
      // NOTHING when suppressed — correct for not scheduling a logout
      // check, but it meant THIS tab's presence entry never got removed
      // either, leftover in Redis for up to 45s. If a cross-origin
      // handoff (this page navigating to a connected app) left this
      // entry behind and the REAL last tab closed within that window,
      // the stale entry still counted as live and wrongly blocked the
      // logout. Always send now; the backend retires the entry
      // immediately either way, just skipping the "schedule a logout"
      // step when suppressed (see tabPresence.js's own comment).
      const suppressed = consumeTabCloseSuppression();
      const payload = new Blob([JSON.stringify({ tabId, suppressed })], { type: 'application/json' });
      // sendBeacon, not fetch: pagehide gives no guarantee a fetch
      // started there survives long enough to actually reach the
      // network — sendBeacon is built specifically to outlive this.
      navigator.sendBeacon(`${API_BASE}/account/tabs/close`, payload);
    }

    heartbeat();
    const intervalId = setInterval(heartbeat, HEARTBEAT_MS);
    // CONFIRMED GAP (2026-10-14), found live on BICC Dashboard: pagehide
    // alone didn't fire when the ENTIRE browser application was closed
    // at once (the only tab/window open) — reliably specified for
    // closing one tab while the browser keeps running, but browsers have
    // historically been less consistent firing it when the whole app
    // quits at once. Also listening on the older sibling event `unload`
    // as a backup — standard cross-browser belt-and-suspenders pattern
    // for exactly this gap. Safe even if both fire for the same close:
    // sending the close signal twice is harmless either way.
    window.addEventListener('pagehide', sendCloseBeacon);
    window.addEventListener('unload', sendCloseBeacon);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('pagehide', sendCloseBeacon);
      window.removeEventListener('unload', sendCloseBeacon);
    };
  }, [user]);

  return null;
}
